import {
  DimeAlert,
  DimeAlertDescription,
  DimeCard,
  DimeCardDescription,
  DimeCardHeader,
  DimeCardTitle,
  DimeDataTable,
  DimeFieldLabel,
  DimeSwitch,
} from '@nudool/design-system';
import { createFileRoute } from '@tanstack/react-router';
import { useMemo, useState } from 'react';
import { useBreadcrumbLabel } from '../../../../components/BreadcrumbLabelsContext.js';
import { ListErrorState } from '../../../../components/ListErrorState.js';
import { EXPORT_PERMISSION } from '../../../../hooks/export-permission.js';
import { useCanExport } from '../../../../hooks/use-can-export.js';
import { useDeniedTooltip } from '../../../../lib/access/index.js';
import { assertUuidRouteParams } from '../../../../lib/assert-uuid-route-params.js';
import { usePermissions } from '../../../../modules/iam/permissions-context.js';
import {
  LeafLinkHeldOrderDialogs,
  type ActiveLeafLinkHeldOrderDialog,
} from '../../../../modules/leaflink-companies/leaflink-held-order-dialogs.js';
import { makeLeafLinkHeldOrderColumns } from '../../../../modules/leaflink-companies/leaflink-held-order-list-columns.js';
import { useLeafLinkCompanyQuery } from '../../../../modules/leaflink-companies/use-leaflink-company-query.js';
import { useLeafLinkHeldOrders } from '../../../../modules/leaflink-companies/use-leaflink-held-orders.js';

const SHOW_RESOLVED_ID = 'leaflink-held-orders-show-resolved';

export const Route = createFileRoute('/integrations/leaflink/$leafLinkIntegrationId/held-orders')({
  beforeLoad: ({ params }) => assertUuidRouteParams(params),
  component: LeafLinkHeldOrdersPage,
});

// eslint-disable-next-line react-refresh/only-export-components
function LeafLinkHeldOrdersPage() {
  const { leafLinkIntegrationId } = Route.useParams();
  const { permissions } = usePermissions();
  const canViewSalesOrders = permissions.includes('salesOrder:view');
  const { canExport } = useCanExport();
  const deniedTooltip = useDeniedTooltip();
  const [activeDialog, setActiveDialog] = useState<ActiveLeafLinkHeldOrderDialog>(null);

  const {
    data: companyData,
    error: companyError,
    refetch: refetchCompany,
  } = useLeafLinkCompanyQuery(leafLinkIntegrationId);
  const company = companyData?.leafLinkCompany ?? null;
  useBreadcrumbLabel(leafLinkIntegrationId, company?.name);

  const heldOrders = useLeafLinkHeldOrders(leafLinkIntegrationId, !canViewSalesOrders);
  const { refetch } = heldOrders;

  const columns = useMemo(
    () =>
      makeLeafLinkHeldOrderColumns({
        onAction: (kind, heldOrder) => setActiveDialog({ kind, heldOrder }),
      }),
    [],
  );

  return (
    <DimeCard borderless className="gap-0 py-0">
      <DimeCardHeader className="px-0 mb-4">
        <DimeCardTitle className="text-base flex items-center gap-2">
          {company ? `${company.name} — Held LeafLink Orders` : 'Held LeafLink Orders'}
        </DimeCardTitle>
        <DimeCardDescription className="text-sm">
          Orders LeafLink sent that need a person to finish importing them. You see the orders of
          brands you belong to; an archived brand's orders appear once it is reactivated.
        </DimeCardDescription>
      </DimeCardHeader>
      {companyError ? (
        <ListErrorState
          title="Could not load this LeafLink company"
          message={companyError.message}
          onRetry={() => void refetchCompany()}
        />
      ) : !canViewSalesOrders ? (
        <DimeAlert className="mb-4">
          <DimeAlertDescription>
            You need permission to view sales orders to see held LeafLink orders.
          </DimeAlertDescription>
        </DimeAlert>
      ) : heldOrders.error ? (
        <ListErrorState
          title="Could not load held LeafLink orders"
          message={heldOrders.error.message}
          onRetry={() => void refetch()}
        />
      ) : (
        <DimeDataTable
          columns={columns}
          data={heldOrders.heldOrders}
          isLoading={heldOrders.loading && !heldOrders.hasData}
          loadingRowCount={heldOrders.pageSize}
          hasNextPage={heldOrders.hasNextPage}
          hasPreviousPage={heldOrders.hasPreviousPage}
          onNextPage={heldOrders.nextPage}
          onPreviousPage={heldOrders.previousPage}
          exportFileName="leaflink-held-orders"
          exportDisabledReason={canExport ? undefined : deniedTooltip(EXPORT_PERMISSION)}
          filters={
            <div className="flex items-center gap-2">
              <DimeSwitch
                id={SHOW_RESOLVED_ID}
                checked={heldOrders.includeResolved}
                onCheckedChange={heldOrders.setIncludeResolved}
              />
              <DimeFieldLabel htmlFor={SHOW_RESOLVED_ID}>Show resolved</DimeFieldLabel>
            </div>
          }
          emptyMessage={
            heldOrders.includeResolved ? 'No held orders.' : 'No held orders are waiting.'
          }
        />
      )}
      <LeafLinkHeldOrderDialogs
        active={activeDialog}
        onClose={() => setActiveDialog(null)}
        onSettled={() => void refetch()}
      />
    </DimeCard>
  );
}
